import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE_URL}/api`;
const TOKEN_KEY = "bj_auth_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

const http = axios.create({ baseURL: API });
http.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
http.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err?.response?.data?.detail;
    return Promise.reject(new Error(detail || err.message || "Request failed"));
  }
);

// Build a displayable URL for an image path returned by the backend.
// Uploaded files live under /api/files/... and need a short-lived token; the
// AuthImage component resolves that. Absolute URLs are returned as-is.
export function isManagedFile(url) {
  return typeof url === "string" && url.includes("/api/files/");
}
export function absoluteFileUrl(pathOrUrl) {
  if (!pathOrUrl) return pathOrUrl;
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  const clean = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${BASE_URL}${clean}`;
}

export const api = {
  base: BASE_URL,

  // Businesses
  listBusinesses: () => http.get("/businesses").then((r) => r.data),
  addCategory: (business_id, label) =>
    http.post(`/businesses/${business_id}/categories`, { label }).then((r) => r.data),
  renameCategory: (business_id, key, label) =>
    http.patch(`/businesses/${business_id}/categories/${encodeURIComponent(key)}`, { label }).then((r) => r.data),
  deleteCategory: (business_id, key) =>
    http.delete(`/businesses/${business_id}/categories/${encodeURIComponent(key)}`).then((r) => r.data),
  pauseOrdering: (business_id, paused) =>
    http.patch(`/businesses/${business_id}/pause`, { paused }).then((r) => r.data),

  // Auth
  signup: (body) => http.post("/auth/signup", body).then((r) => r.data),
  login: (body) => http.post("/auth/login", body).then((r) => r.data),
  googleSession: (session_id, business_id) =>
    http.post("/auth/google/session", { session_id, business_id }).then((r) => r.data),
  forgotPassword: (email) => http.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (email, code, new_password) =>
    http.post("/auth/reset-password", { email, code, new_password }).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  deleteAccount: () => http.delete("/auth/me").then((r) => r.data),
  bakerSlots: () => http.get("/auth/baker-slots").then((r) => r.data),

  // Admin: account management (baker only)
  listUsers: () => http.get("/admin/users").then((r) => r.data),
  deleteUser: (id) => http.delete(`/admin/users/${id}`).then((r) => r.data),
  deleteAllUsers: () => http.delete("/admin/users").then((r) => r.data),

  // Products
  listProducts: (business_id, category, include_hidden = false) => {
    const q = new URLSearchParams({ business_id });
    if (category && category !== "all") q.set("category", category);
    if (include_hidden) q.set("include_hidden", "true");
    return http.get(`/products?${q.toString()}`).then((r) => r.data);
  },
  getProduct: (id) => http.get(`/products/${id}`).then((r) => r.data),
  getSideOptions: (id) => http.get(`/products/${id}/side-options`).then((r) => r.data),
  createProduct: (body) => http.post("/products", body).then((r) => r.data),
  createProductsBulk: (products) => http.post("/products/bulk", { products }).then((r) => r.data),
  reorderProducts: (business_id, ordered_ids) =>
    http.patch("/products/reorder", { business_id, ordered_ids }).then((r) => r.data),
  updateProduct: (id, body) => http.patch(`/products/${id}`, body).then((r) => r.data),
  deleteProduct: (id) => http.delete(`/products/${id}`).then((r) => r.data),

  // Orders
  createOrder: (body) => http.post("/orders", body).then((r) => r.data),
  listOrders: (status, business_id) => {
    const q = new URLSearchParams();
    if (business_id) q.set("business_id", business_id);
    if (status && status !== "all") q.set("status", status);
    const qs = q.toString();
    return http.get(`/orders${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  getOrder: (id) => http.get(`/orders/${id}`).then((r) => r.data),
  lookupOrders: (params) => {
    const q = new URLSearchParams();
    if (params.business_id) q.set("business_id", params.business_id);
    if (params.phone) q.set("phone", params.phone);
    if (params.order_number) q.set("order_number", params.order_number);
    if (params.mine) q.set("mine", "true");
    return http.get(`/orders/lookup?${q.toString()}`).then((r) => r.data);
  },
  updateOrderStatus: (id, status) =>
    http.patch(`/orders/${id}/status`, { status }).then((r) => r.data),
  deleteOrder: (id) => http.delete(`/orders/${id}`).then((r) => r.data),
  clearOrders: (business_id) =>
    http
      .delete(`/orders/all${business_id ? `?business_id=${encodeURIComponent(business_id)}` : ""}`)
      .then((r) => r.data),
  overrideDeposit: (id, body) => http.patch(`/orders/${id}/deposit`, body).then((r) => r.data),
  markBalancePaid: (id, method = "cash") =>
    http.post(`/orders/${id}/mark-balance-paid?method=${encodeURIComponent(method)}`, {}).then((r) => r.data),
  setQuotePrice: (id, items) =>
    http.patch(`/orders/${id}/quote`, { items }).then((r) => r.data),
  setOrderTotal: (id, total) =>
    http.patch(`/orders/${id}/quote-total`, { total }).then((r) => r.data),

  // Payments
  createCheckoutSession: (order_id, success_url, cancel_url) =>
    http.post("/payments/checkout", { order_id, success_url, cancel_url }).then((r) => r.data),
  createBalanceCheckoutSession: (order_id, success_url, cancel_url) =>
    http.post(`/payments/pay-balance/${order_id}`, { success_url, cancel_url }).then((r) => r.data),
  getPaymentStatus: (order_id) => http.get(`/payments/status/${order_id}`).then((r) => r.data),

  // Payment info
  getPaymentInfo: (business_id) =>
    http.get(`/payment-info?business_id=${encodeURIComponent(business_id)}`).then((r) => r.data),
  updatePaymentInfo: (body, business_id) =>
    http
      .patch(`/payment-info${business_id ? `?business_id=${encodeURIComponent(business_id)}` : ""}`, body)
      .then((r) => r.data),
  getNotifyPrefs: () => http.get("/baker/notify-prefs").then((r) => r.data),
  updateNotifyPrefs: (body) => http.patch("/baker/notify-prefs", body).then((r) => r.data),
  updateHomeBusiness: (business_id) => http.patch("/baker/home-business", { business_id }).then((r) => r.data),

  // Stats & reports
  getStats: (business_id) =>
    http.get(`/stats${business_id ? `?business_id=${encodeURIComponent(business_id)}` : ""}`).then((r) => r.data),
  getSalesReport: ({ business_id, days }) => {
    const q = new URLSearchParams();
    if (business_id) q.set("business_id", business_id);
    if (days) q.set("days", String(days));
    return http.get(`/reports/sales?${q.toString()}`).then((r) => r.data);
  },

  // Reminders
  getBalanceDueReminders: (hoursWindow = 24) =>
    http.get(`/reminders/balance-due?hours_window=${hoursWindow}`).then((r) => r.data),

  // Specials
  listSpecials: (business_id, opts = {}) => {
    const q = new URLSearchParams({ business_id });
    if (opts.include_ended === false) q.set("include_ended", "false");
    if (opts.active_only) q.set("active_only", "true");
    return http.get(`/specials?${q.toString()}`).then((r) => r.data);
  },
  getSpecial: (id) => http.get(`/specials/${id}`).then((r) => r.data),
  createSpecial: (body) => http.post("/specials", body).then((r) => r.data),
  updateSpecial: (id, body) => http.patch(`/specials/${id}`, body).then((r) => r.data),
  deleteSpecial: (id) => http.delete(`/specials/${id}`).then((r) => r.data),

  // Loyalty
  getLoyalty: () => http.get("/loyalty/me").then((r) => r.data),
  redeemVoucher: (code) => http.post("/loyalty/vouchers/redeem", { code }).then((r) => r.data),

  // File token (for displaying uploaded images in <img>)
  fileToken: (path) => http.get(`/files/token?path=${encodeURIComponent(path)}`).then((r) => r.data),

  // Image upload (multipart)
  uploadImage: async (file) => {
    const form = new FormData();
    form.append("file", file, file.name || `photo-${Date.now()}.jpg`);
    const t = getToken();
    const res = await fetch(`${API}/upload`, {
      method: "POST",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: form,
    });
    if (!res.ok) {
      let detail = "";
      try {
        detail = (await res.json())?.detail || "";
      } catch (e) {}
      throw new Error(detail || `Upload failed ${res.status}`);
    }
    return res.json(); // { path, url, size, content_type }
  },
};
